import React from 'react';
import { Box, Tooltip, Typography, Chip } from '@mui/material';
import { PaymentStatus as PaymentStatusType } from '../../types/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

interface PaymentStatusProps {
  status: PaymentStatusType;
  amount?: number;
  expectedAmount?: number;
  currentExpectedAmount?: number; // Aktueller Betrag aus Meldung
  compact?: boolean;
  showInconsistency?: boolean; // Zeige Inkonsistenz-Warnung
}

const PaymentStatus: React.FC<PaymentStatusProps> = ({
  status,
  amount = 0,
  expectedAmount = 0,
  currentExpectedAmount,
  compact = false,
  showInconsistency = true
}) => {
  // Verwende aktuellen Betrag wenn verfügbar, sonst gespeicherten
  const effectiveExpectedAmount = currentExpectedAmount ?? expectedAmount;
  
  // Berechne echten Status basierend auf aktuellem Betrag
  const calculateRealStatus = (): PaymentStatusType => {
    // Sicherstellen dass beide Werte Numbers sind und auf 2 Dezimalstellen runden
    const paidRounded = Number((amount || 0).toFixed(2));
    const expectedRounded = Number(effectiveExpectedAmount.toFixed(2));
    if (paidRounded === expectedRounded) {
      return 'paid';
    } else if (paidRounded > 0) {
      return 'partial';
    } else {
      return 'unpaid';
    }
  };

  const realStatus = currentExpectedAmount ? calculateRealStatus() : status;
  const hasInconsistency = showInconsistency && currentExpectedAmount && realStatus !== status;

  let color: 'success' | 'warning' | 'error';
  let icon;
  let label: string;
  let tooltipText: string;

  switch (realStatus) {
    case 'paid':
      color = 'success';
      icon = <CheckCircleIcon />;
      label = 'Bezahlt';
      tooltipText = `Vollständig bezahlt: ${amount.toFixed(2)}€ von ${effectiveExpectedAmount.toFixed(2)}€`;
      break;
    case 'partial':
      color = 'warning';
      icon = <WarningIcon />;
      label = 'Teilzahlung';
      tooltipText = `Teilweise bezahlt: ${amount.toFixed(2)}€ von ${effectiveExpectedAmount.toFixed(2)}€`;
      break;
    case 'unpaid':
    default:
      color = 'error';
      icon = <ErrorIcon />;
      label = 'Unbezahlt';
      tooltipText = `Nicht bezahlt: ${effectiveExpectedAmount.toFixed(2)}€ ausstehend`;
      break;
  }

  // Erweiterte Tooltip-Info bei Inkonsistenz
  if (hasInconsistency) {
    tooltipText += `\n⚠️ ACHTUNG: Gespeicherter Status (${status}) stimmt nicht mit aktuellem Meldebetrag überein!\nGespeichert: ${expectedAmount.toFixed(2)}€, Aktuell: ${currentExpectedAmount!.toFixed(2)}€`;
  }

  if (compact) {
    return (
      <Tooltip title={tooltipText}>
        <Box sx={{ position: 'relative' }}>
          <Chip
            icon={icon}
            label={label}
            color={color}
            size="small"
          />
          {/* Inkonsistenz-Indikator */}
          {hasInconsistency && (
            <Box 
              sx={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: 8,
                height: 8,
                bgcolor: 'warning.main',
                borderRadius: '50%',
                border: '1px solid white'
              }}
            />
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipText}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: `${color}.light`,
          color: `${color}.dark`,
          px: 2,
          py: 1,
          borderRadius: 1,
          width: 'fit-content',
          position: 'relative'
        }}
      >
        {icon}
        <Typography variant="body2" fontWeight="medium">
          {label}
        </Typography>
        <Typography variant="body2">
          {realStatus === 'unpaid' 
            ? `(${effectiveExpectedAmount.toFixed(2)}€)` 
            : realStatus === 'partial'
              ? `(${amount.toFixed(2)}€ / ${effectiveExpectedAmount.toFixed(2)}€)`
              : `(${amount.toFixed(2)}€)`}
        </Typography>
        
        {/* Inkonsistenz-Warnung */}
        {hasInconsistency && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'warning.main', 
              fontWeight: 'bold',
              fontSize: '0.7rem'
            }}
          >
            ⚠️
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default PaymentStatus;